import {
  AutoClosingDropdown,
  DateRangeDropdown,
  Dropdown,
  EyeCrossedIcon,
  EyeIcon,
  RecurringIcon,
  SearchBar,
  SmallButton,
  TimePicker,
  UndoIcon,
} from "@wit/mpesa-ui-components";
import styleTheme from "@wit/mpesa-ui-components/lib/configs/theme.config";
import { FilterFuncts } from "@wit/mpesa-ui-components/lib/hooks/use-filters.hook";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { RatioContainer, Row } from "../../../../shared/shared.styled";
import { MiniAppsLogStatusEnum } from "../../support.interfaces";
import { onEnterPressed } from "../../../../shared/shared.utils";

export enum MiniAppsFilterKeys {
  STATUS = "requestStatus",
  OPERATION = "serviceOperation",
  TIME_RANGE = "timeRange",
  START_DATE = "startDate",
  END_DATE = "endDate",
  REQUEST_ID = "requestId",
  REQUEST_CONTENT = "requestContent",
  SYNC_RESPONSE_CONTENT = "syncResponseContent",
}

interface IMiniAppsLogsFiltersProps extends FilterFuncts {
  miniAppsLogsCount: number;
  refreshResults: () => void;
}

/**
 * Mini Apps logs filters
 * @param param0
 * @returns
 */
const MiniAppsLogsFilters = ({
  filters,
  clearFilter,
  isFilterActive,
  resetFilters,
  updateFilter,
  updateMultipleFilters,
  miniAppsLogsCount,
  refreshResults,
}: IMiniAppsLogsFiltersProps) => {
  const [t] = useTranslation();
  const [timeValues, setTimeValues] = React.useState({
    fromValue: "00:00",
    toValue: "18:00",
  });
  const [isTimePickerOpen, setIsTimePickerOpen] = React.useState(false);
  const firstUpdate = React.useRef(true);
  const [showExtraFields, setShowExtraFields] = React.useState(false);

  /*
   * Clear the hidden search fields upon hiding them
   * */
  React.useEffect(() => {
    if (!showExtraFields) {
      updateMultipleFilters([
        { key: MiniAppsFilterKeys.REQUEST_CONTENT, values: [""] },
        { key: MiniAppsFilterKeys.SYNC_RESPONSE_CONTENT, values: [""] },
      ]);
    }
  }, [showExtraFields]);

  /*
   * Update time range filter upon timeValues change
   * */
  React.useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    updateMultipleFilters([{ key: MiniAppsFilterKeys.TIME_RANGE, values: [timeValues.fromValue, timeValues.toValue] }]);
  }, [timeValues]);

  return (
    <>
      <CustomRow>
        <RatioContainer ratio={3 / 12}>
          <SearchBar
            onKeyDown={e => {
              onEnterPressed(e, refreshResults);
            }}
            value={
              filters.has(MiniAppsFilterKeys.REQUEST_ID)
                ? (filters.get(MiniAppsFilterKeys.REQUEST_ID) as string[])[0]
                : ""
            }
            onChange={e => updateFilter(MiniAppsFilterKeys.REQUEST_ID, e.target.value, true)}
            clearValue={() => clearFilter(MiniAppsFilterKeys.REQUEST_ID)}
            placeholderLabel={t("pages.support.miniAppsLogs.filters.requestId")}
          />
        </RatioContainer>
        <RatioContainer ratio={3 / 12}>
          <SearchBar
            onKeyDown={e => {
              onEnterPressed(e, refreshResults);
            }}
            value={
              filters.has(MiniAppsFilterKeys.OPERATION)
                ? (filters.get(MiniAppsFilterKeys.OPERATION) as string[])[0]
                : ""
            }
            onChange={e => updateFilter(MiniAppsFilterKeys.OPERATION, e.target.value, true)}
            clearValue={() => clearFilter(MiniAppsFilterKeys.OPERATION)}
            placeholderLabel={t("pages.support.miniAppsLogs.filters.miniAppsOperation")}
          />
        </RatioContainer>
        <RatioContainer ratio={2 / 12}>
          <DateRangeDropdown
            filters={filters}
            isFilterActive={isFilterActive}
            clearFilter={clearFilter}
            updateFilter={updateFilter}
            updateMultipleFilters={updateMultipleFilters}
          />
        </RatioContainer>
        <RatioContainer ratio={2 / 12}>
          <Dropdown
            controlledToggleOpen={setIsTimePickerOpen}
            isOpen={isTimePickerOpen}
            hasValue={filters.has(MiniAppsFilterKeys.TIME_RANGE)}
            label={
              filters.has(MiniAppsFilterKeys.TIME_RANGE)
                ? `${(filters.get(MiniAppsFilterKeys.TIME_RANGE) as string[])[0]} - ${
                    (filters.get(MiniAppsFilterKeys.TIME_RANGE) as string[])[1]
                  }`
                : t("pages.support.miniAppsLogs.filters.timeRange")
            }
          >
            {isTimePickerOpen ? (
              <TimePickerContainer>
                <TimePicker values={timeValues} setValues={setTimeValues} />
              </TimePickerContainer>
            ) : null}

            <ClearFilterText
              onClick={() => {
                setTimeValues({
                  fromValue: "00:00",
                  toValue: "18:00",
                });
                setTimeout(() => {
                  setIsTimePickerOpen(false);
                  clearFilter(MiniAppsFilterKeys.TIME_RANGE);
                }, 0);
              }}
            >
              {t("pages.support.miniAppsLogs.filters.clearFilter")}
            </ClearFilterText>
          </Dropdown>
        </RatioContainer>
        <RatioContainer ratio={2 / 12}>
          <AutoClosingDropdown
            options={Object.values(MiniAppsLogStatusEnum)
              .filter(s => typeof s === "string")
              .map(status => {
                return {
                  label: t(`commons.miniAppsLogStatus.${status}`),
                  key: status,
                };
              })}
            selectOption={opt => {
              updateFilter(MiniAppsFilterKeys.STATUS, opt.key, true);
            }}
            hasValue={!!filters.get(MiniAppsFilterKeys.STATUS)}
            label={
              filters.get(MiniAppsFilterKeys.STATUS)
                ? t(`commons.miniAppsLogStatus.${(filters.get(MiniAppsFilterKeys.STATUS) as string[])[0]}`)
                : t("pages.support.miniAppsLogs.filters.status")
            }
          ></AutoClosingDropdown>
        </RatioContainer>
      </CustomRow>
      <CustomRow style={{ display: showExtraFields ? "inherit" : "none" }}>
        <RatioContainer ratio={4 / 12}>
          <SearchBar
            onKeyDown={e => {
              onEnterPressed(e, refreshResults);
            }}
            value={
              filters.has(MiniAppsFilterKeys.REQUEST_CONTENT)
                ? (filters.get(MiniAppsFilterKeys.REQUEST_CONTENT) as string[])[0]
                : ""
            }
            onChange={e => updateFilter(MiniAppsFilterKeys.REQUEST_CONTENT, e.target.value, true)}
            clearValue={() => clearFilter(MiniAppsFilterKeys.REQUEST_CONTENT)}
            placeholderLabel={t("pages.support.miniAppsLogs.filters.requestContent")}
          />
        </RatioContainer>
        <RatioContainer ratio={4 / 12}>
          <SearchBar
            onKeyDown={e => {
              onEnterPressed(e, refreshResults);
            }}
            value={
              filters.has(MiniAppsFilterKeys.SYNC_RESPONSE_CONTENT)
                ? (filters.get(MiniAppsFilterKeys.SYNC_RESPONSE_CONTENT) as string[])[0]
                : ""
            }
            onChange={e => updateFilter(MiniAppsFilterKeys.SYNC_RESPONSE_CONTENT, e.target.value, true)}
            clearValue={() => clearFilter(MiniAppsFilterKeys.SYNC_RESPONSE_CONTENT)}
            placeholderLabel={t("pages.support.miniAppsLogs.filters.syncResponseContent")}
          />
        </RatioContainer>
      </CustomRow>
      <CustomRow>
        <SmallButton
          titleLabel={t(`pages.support.miniAppsLogs.${showExtraFields ? "hide" : "show"}ExtraFields`)}
          onClick={() => setShowExtraFields(!showExtraFields)}
          iconComponent={
            <SmallButtonIconContainer color={styleTheme.palette.turquoiseBlue}>
              {showExtraFields ? <EyeCrossedIcon /> : <EyeIcon />}
            </SmallButtonIconContainer>
          }
        />
      </CustomRow>
      <Row style={{ marginBottom: 12 }}>
        <MiniAppsLogsCount>
          <span>{miniAppsLogsCount}</span>
          &nbsp;
          {`${t("pages.support.miniAppsLogs.recordsFound")}`}
        </MiniAppsLogsCount>
        <div style={{ marginRight: 24 }}>
          <SmallButton
            titleLabel={t("pages.support.miniAppsLogs.refreshResults")}
            onClick={() => refreshResults()}
            iconComponent={
              <SmallButtonIconContainer color={styleTheme.palette.turquoiseBlue}>
                <RecurringIcon />
              </SmallButtonIconContainer>
            }
          />
        </div>
        <div>
          <SmallButton
            titleLabel={t("pages.support.miniAppsLogs.clearFilters")}
            onClick={() => {
              setTimeValues({
                fromValue: "00:00",
                toValue: "18:00",
              });
              setTimeout(() => {
                setIsTimePickerOpen(false);
                clearFilter(MiniAppsFilterKeys.TIME_RANGE);
                resetFilters();
              }, 0);
            }}
            iconComponent={
              <SmallButtonIconContainer color={styleTheme.palette.vodafoneRed}>
                <UndoIcon />
              </SmallButtonIconContainer>
            }
          />
        </div>
      </Row>
    </>
  );
};

export default MiniAppsLogsFilters;

const TimePickerContainer = styled("div")`
  padding: 8px 8px 8px 12px;
`;

const ClearFilterText = styled("div")`
  font-family: Vodafone Rg;
  font-size: 14px;
  color: ${props => props.theme.palette.midGrey};
  text-align: left;
  display: flex;
  align-items: center;
  max-height: 30px;
  height: 30px;
  margin-top: 8px;
  margin-bottom: 8px;
  padding-left: 12px;
  &:hover {
    background: ${props => props.theme.palette.lightGrey};
  }
`;

const CustomRow = styled(Row)`
  margin-bottom: 18px;

  > ${RatioContainer} {
    margin-right: 24px;

    :last-of-type {
      margin-right: 0;
    }
  }
`;

const MiniAppsLogsCount = styled("div")`
  display: flex;
  flex-direction: row;
  margin-right: 24px;
  font-family: Vodafone Rg;
  font-size: 14px;
  color: ${props => props.theme.palette.midGrey};

  > span {
    font-weight: bold;
  }
`;

const SmallButtonIconContainer = styled("div")<{ color: string }>`
  width: 16px;
  height: 16px;
  stroke: ${props => props.color};

  svg {
    width: 16px;
    height: 16px;
    stroke: ${props => props.color};
  }
`;
